import { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { StyleSheet, Text, View } from 'react-native';

import BrandedButton from '@/components/buttons/BrandedButton';
import BankRoutingNumberInput from '@/components/inputs/BankRoutingNumberInput';
import TextInputField from '@/components/inputs/TextInputField';
import SaveAndContinueLaterButton from '@/components/npo/onboarding/SaveAndContinueLaterButton';
import { BankAccountData, bankAccountResolver } from '@/lib/validations/NpoPaymentOnboarding';
import { i18n } from '@/utils/i18n';

interface BankAccountFormProps {
  initialData?: BankAccountData;
  onDone?: (data: BankAccountData) => void;
  onSaveAndContinue?: (data: BankAccountData) => void;
  doneLabel?: string;
  isLoading?: boolean;
}

const BankAccountForm = ({
  initialData,
  onDone,
  onSaveAndContinue,
  doneLabel = i18n.t('mix.done'),
  isLoading = false
}: BankAccountFormProps) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid, errors, isDirty },
    clearErrors,
    setError,
    setFocus
  } = useForm<BankAccountData>({
    defaultValues: initialData,
    mode: 'onChange',
    resolver: bankAccountResolver
  });

  const onSubmit: SubmitHandler<BankAccountData> = async (data) => {
    if (onDone) {
      onDone(data);
    }
  };

  const onSaveAndContinueHandler: SubmitHandler<BankAccountData> = async (data) => {
    if (onSaveAndContinue) {
      onSaveAndContinue(data);
    }
  };

  useEffect(() => {
    if (initialData && !isDirty) {
      reset(initialData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  return (
    <View style={styles.formContainer}>
      <View style={styles.inputsContainer}>
        <BankRoutingNumberInput
          control={control}
          name="bankRoutingNumber"
          setError={setError}
          clearErrors={clearErrors}
          returnKeyLabel="next"
          returnKeyType="next"
          onSubmitEditing={() => setFocus('bankAccountNumber')}
        />
        <TextInputField
          name="bankAccountNumber"
          control={control}
          placeholder={i18n.t('npoPaymentOnboarding.bank_account_form.bankAccountNumber_placeholder')}
          returnKeyLabel="done"
          returnKeyType="done"
          type="numeric-string"
          label={i18n.t('npoPaymentOnboarding.bank_account_form.bankAccountNumber_label')}
        />
      </View>
      {errors.root !== null && <Text style={styles.errorText}>{errors?.root?.message}</Text>}

      <View style={styles.buttonsContainer}>
        <BrandedButton loading={isLoading} onPress={handleSubmit(onSubmit)} disabled={!isValid}>
          {doneLabel}
        </BrandedButton>
        <SaveAndContinueLaterButton onPress={handleSubmit(onSaveAndContinueHandler)} />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  formContainer: {
    flex: 1,
    justifyContent: 'space-between'
  },

  inputsContainer: {
    gap: 15
  },

  errorText: {
    color: 'red',
    fontSize: 20,
    fontWeight: 'bold'
  },

  buttonsContainer: {
    width: '100%'
  }
});

export default BankAccountForm;
